// src/components/InHouseTab.jsx
import React, { useState, useEffect, useRef } from "react";
import { Spinner } from 'react-bootstrap';
import { FaHotel } from 'react-icons/fa';
import CryptoJS from "crypto-js"; // Import CryptoJS for encryption
import { Link, useNavigate } from "react-router-dom";
import { renderDate } from "../utils/dateFormatter";

const InHouseTab = ({ businessDate }) => {
  const [inHouseData, setInHouseData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const tabRef = useRef(null);

  // Define apiURL and token directly within the component
  const apiURL = process.env.REACT_APP_API_URL || "https://api.example.com";
  const token = localStorage.getItem("token");
  const secretKey = process.env.REACT_APP_ENCRYPTION_SECRET_KEY || "abde100199199111";

  useEffect(() => {
    let observer;

    const loadInHouseData = async () => {
      setIsLoading(true); // Set loading to true before fetching
      try {
        console.log('Attempting to fetch in-house reservations for date:', businessDate);
        const response = await fetch(`${apiURL}/booking/reservations?inhouse=true&date=${businessDate}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();

        // Transform the API response to match the structure (similar to Arrivals/Departures)
        const transformedData = data.reservations.map(reservation => ({
          name: reservation.guest_name,
          phone: reservation.guest_phone,
          accommodation: `${reservation.room_types} - ${reservation.room_numbers}`,
          booking_id: reservation.booking_id,
          booking_status: reservation.booking_status,
          stay: {
            nights: Math.ceil((new Date(reservation.check_out_date) - new Date(reservation.check_in_date)) / (1000 * 60 * 60 * 24)), // Calculate nights
            adults: 1, // Adjust based on your data (e.g., fetch adults/children from another field or API)
            children: 0, // Adjust as needed
          },
          checkIn: renderDate(reservation.check_in_date),
          checkOut: renderDate(reservation.check_out_date),
          status: reservation.booking_status === 'reserved' ? 'Ready' : 
                  reservation.booking_status === 'cancelled' ? 'Cancelled' : 
                  'Check-In', // Map booking_status to your status format for in-house guests
        }));

        setInHouseData(transformedData);
      } catch (error) {
        console.error('Error fetching in-house reservations:', error);
        setInHouseData([]); // Fallback to empty array on error
      } finally {
        setIsLoading(false); // Set loading to false after fetching
      }
    };

    // Lazy loading with Intersection Observer (triggers when tab content is visible)
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        console.log('In-House tab section is intersecting, triggering in-house API call');
        loadInHouseData();
        observer.unobserve(tabRef.current);
      }
    }, options);

    // Observe the tab content
    if (tabRef.current) {
      observer.observe(tabRef.current);
    } else {
      console.warn('tabRef.current is null, retrying in 1 second for in-house...');
      setTimeout(() => observer.observe(tabRef.current), 1000);
    }

    // Fallback: Call the API after 2 seconds if IntersectionObserver doesn’t trigger
    const fallbackTimer = setTimeout(() => {
      if (isLoading) {
        console.log('Falling back to direct in-house API call due to no intersection');
        loadInHouseData();
      }
    }, 2000);

    return () => {
      if (observer && tabRef.current) {
        observer.unobserve(tabRef.current);
      }
      clearTimeout(fallbackTimer);
    };
  }, [apiURL, token, businessDate]); // Dependency on businessDate to re-fetch when it changes

  return (
    <div ref={tabRef}>
      {isLoading ? (
        <div className="text-center py-4">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Loading in-house data...</p>
        </div>
      ) : inHouseData.length > 0 ? (
        <table className="table">
        <thead>
          <tr>
            <th>Guest</th>
            <th>Accommodation</th>
            <th>Stay</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {inHouseData.map((guest, index) => {
            // Encrypt the booking_id
            const encryptedId = CryptoJS.AES.encrypt(
              guest.booking_id.toString(),
              secretKey
            ).toString();

            return (
              <tr key={index}>
                <td>
                  <Link
                    to={`/hospitality/reservation-details/${encodeURIComponent(encryptedId)}`}
                    className="colored-box reservation-no-box"
                    style={{ minWidth: "0px" }}
                  >
                    {guest.name}
                  </Link>
                </td>
                <td>{guest.accommodation}</td>
                <td>
                  {guest.checkIn} → {guest.checkOut}
                  <p>
                    {guest.stay.nights} Night(s), {guest.stay.adults} Adult(s),{" "}
                    {guest.stay.children} Child(ren)
                  </p>
                </td>
                <td>
                  <button className="btn bg-success" style={{
            display: "inline-block",
            padding: "4px 12px",
            borderRadius: "4px",
            color: "#fff",
            fontSize: "12px",
            fontWeight: "500",
            marginTop: "8px",
            textTransform: "capitalize",
          }}>In-House</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      ) : (
        <div className="text-center py-4 text-muted">
          <FaHotel size={30} className="mb-2" />
          <p>No In-House Guests</p>
        </div>
      )}
    </div>
  );
};

export default InHouseTab;